import {createSlice, isFulfilled, isPending, isRejected, PayloadAction} from "@reduxjs/toolkit";
import {IInvoice, IInvoiceStatistics} from "../../../models/iInvoices";
import {ISelectedOrderPosition} from "../../../models/IOrdersPositions";
import {
    fetchAddInvoice,
    fetchGetAllInvoices,
    fetchGetInvoiceById, fetchGetInvoicesForNewShipment, fetchGetInvoicesStatistics,
    fetchUpdateInvoice,
    fetchUploadInvoice,
    fetchUploadPayment
} from "./actions";

const handledThunks = [
    fetchAddInvoice,
    fetchGetAllInvoices,
    fetchUpdateInvoice,
    fetchGetInvoiceById,
    fetchUploadInvoice,
    fetchUploadPayment,
    fetchGetInvoicesForNewShipment,
] as const;

interface IInvoiceState {
    list: IInvoice[];
    current: IInvoice | null;
    isLoading: boolean;
    selectedPosition: ISelectedOrderPosition;
    statistics: IInvoiceStatistics | null;
}

const initialState: IInvoiceState = {
    list: [],
    current: null,
    isLoading: false,
    selectedPosition: {},
    statistics: null,
};

export const InvoicesSlice = createSlice({
    name: "invoices",
    initialState,
    reducers: {
        setInvoices: (state, action: PayloadAction<IInvoice[]>) => {
            state.list = action.payload;
        },
        addInvoices: (state, action: PayloadAction<IInvoice[]>) => {
            state.list = [...action.payload, ...state.list];
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchAddInvoice.fulfilled, (state, action: PayloadAction<IInvoice>) => {
                state.list = [...state.list, action.payload];
            })
            .addCase(fetchGetAllInvoices.fulfilled, (state, action: PayloadAction<IInvoice []>) => {
                state.list = action.payload.sort((a, b) => {
                    return b.author_date - a.author_date
                });
            })
            .addCase(fetchGetInvoicesForNewShipment.fulfilled, (state, action: PayloadAction<IInvoice []>) => {
                state.list = action.payload.sort((a, b) => {
                    return b.author_date - a.author_date
                });
            })
            .addCase(fetchUpdateInvoice.fulfilled, (state, action: PayloadAction<IInvoice>) => {
                state.current = action.payload;
                state.list = [...state.list.map(invoice => invoice.id === action.payload.id ? action.payload : invoice)];
            })
            .addCase(fetchGetInvoicesStatistics.fulfilled, (state, action: PayloadAction<IInvoiceStatistics>) => {
                state.statistics = action.payload;
            })
            .addCase(fetchGetInvoiceById.fulfilled, (state, action: PayloadAction<IInvoice>) => {
                state.current = action.payload;
            })
            .addMatcher(isPending(...handledThunks), (state) => {
                state.isLoading = true;
            })
            .addMatcher(isRejected(...handledThunks), (state) => {
                state.isLoading = false;
            })
            .addMatcher(isFulfilled(...handledThunks), (state) => {
                state.isLoading = false;
            });
    },
});

export const {setInvoices, addInvoices} = InvoicesSlice.actions;

export default InvoicesSlice.reducer;
